import type { CSSProperties } from 'react';
import {
  LINE_HEIGHT_RATIO,
  TYPED_SIGNATURE_FONT,
  alignToJustify,
  checkMarkSize,
  previewFontFamily,
  signerTextFontSize,
  typedSignatureFontSize,
} from '@tes/domain';
import { CheckMark } from '@tes/ui';
import type { DefaultTextStyle, VariableType } from '../lib/types';
import type { EditorField } from './editorStore';

/**
 * A ticked checkbox, exactly where and how the PDF engine will stamp it — same
 * X, same size rule (checkMarkSize), same horizontal alignment. Previewing the
 * ticked state rather than an empty box is deliberate: an unticked box stamps
 * nothing at all, and a field you can't see is a field you can't position.
 */
function CheckboxPreview({
  field,
  zoom,
  color,
  justify,
}: {
  field: EditorField;
  zoom: number;
  color: string;
  justify: CSSProperties['justifyContent'];
}) {
  const box = Math.max(6, checkMarkSize(field.w, field.h) * zoom);
  return (
    <div className="pointer-events-none flex h-full w-full items-center" style={{ justifyContent: justify }}>
      <CheckMark size={box} color={color} />
    </div>
  );
}

function initialsOf(label: string): string {
  const letters = label
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase())
    .join('');
  return (letters || 'AB').slice(0, 3);
}

/**
 * Renders a placed field as it will look on the finished document:
 *   variable   → the key, in its effective merge style (checkbox vars → the mark)
 *   static     → its own literal text, exactly as it will merge
 *   date       → the platform date pattern, e.g. DD/MM/YYYY (auto + picker)
 *   text       → sample text
 *   signature  → the role name in a typed-signature style (italic serif)
 *   initials   → derived initials, same style
 *   checkbox   → the X, exactly where and how the stamp engine will draw it
 * Effective style = the template default overridden by the field's own
 * font/fontSize/fontColor/align — the same resolution the merge and stamp
 * engines apply. Sizes track page zoom (1pt ≈ 1px at 100%).
 */
export function FieldPreview({
  field,
  zoom,
  style,
  datePattern,
  roleLabel,
  variableType,
}: {
  field: EditorField;
  zoom: number;
  style: DefaultTextStyle;
  datePattern: string;
  roleLabel: string;
  /** For `kind: 'variable'` — the library entry's type, read live by the canvas. */
  variableType?: VariableType;
}) {
  const eff = {
    font: field.font ?? style.font,
    size: field.fontSize ?? style.size,
    color: field.fontColor ?? style.color,
    align: field.align ?? style.align,
  };

  // Horizontal justification follows the field's effective align, matching the
  // stamp engine (alignX). Vertical placement stays centered.
  const justify = alignToJustify(eff.align);

  // Static text and variables render identically — same merge engine, same
  // wrapping. The only difference is where the string comes from.
  if (field.kind === 'variable' || field.kind === 'static') {
    // A checkbox variable merges as a mark, not as its value — preview the mark.
    if (field.kind === 'variable' && variableType === 'checkbox') {
      return <CheckboxPreview field={field} zoom={zoom} color={eff.color} justify={justify} />;
    }
    return (
      <div
        className="pointer-events-none flex h-full w-full flex-col overflow-hidden"
        style={{
          fontFamily: previewFontFamily(eff.font),
          fontSize: Math.max(4, eff.size * zoom),
          lineHeight: LINE_HEIGHT_RATIO,
          color: eff.color,
          textAlign: eff.align,
          justifyContent: field.multiline ? 'flex-start' : 'center',
        }}
      >
        <span
          style={{
            width: '100%',
            overflow: 'hidden',
            whiteSpace: field.multiline ? 'normal' : 'nowrap',
            textOverflow: field.multiline ? 'clip' : 'ellipsis',
            wordBreak: field.multiline ? 'break-word' : 'normal',
          }}
        >
          {field.kind === 'static' ? (field.text ?? '') : (field.key ?? 'variable')}
        </span>
      </div>
    );
  }

  if (field.type === 'checkbox') return <CheckboxPreview field={field} zoom={zoom} color={eff.color} justify={justify} />;

  // date / text / signature / initials — sample content sized like the stamp.
  const base: CSSProperties = {
    width: '100%',
    color: eff.color,
    lineHeight: 1.1,
    textAlign: eff.align,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  };

  let inner: React.ReactNode;
  if (field.type === 'date' || field.type === 'text') {
    // The stamp's start size (shared rule): the field's own fontSize, else
    // auto from box height.
    const size = signerTextFontSize(field.h, field.fontSize) * zoom;
    inner = (
      <span style={{ ...base, fontFamily: previewFontFamily(eff.font), fontSize: size }}>
        {field.type === 'date' ? datePattern : 'Sample text'}
      </span>
    );
  } else {
    // signature / initials → the typed-signature face the signing page
    // rasterises (stamped as an image; only align applies, so font/size/color
    // overrides are ignored here on purpose)
    inner = (
      <span
        style={{
          ...base,
          color: TYPED_SIGNATURE_FONT.color,
          fontFamily: TYPED_SIGNATURE_FONT.family,
          fontStyle: TYPED_SIGNATURE_FONT.style,
          fontSize: typedSignatureFontSize(field.h) * zoom,
        }}
      >
        {field.type === 'initials' ? initialsOf(roleLabel) : roleLabel || 'Signature'}
      </span>
    );
  }

  return <div className="pointer-events-none flex h-full w-full items-center">{inner}</div>;
}
